import * as React from "react"
import { useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"

import { faArrowRight } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import classNames from "classnames"
import api, { ApiFieldError } from "../../../services/api"
import Input from "../../components/input"
import { useAuth } from "../../hooks/auth"

export default function RegistrationPage() {
  let [searchParams, _setSearchParams] = useSearchParams()
  const initSignupCode = searchParams.get("signup_code")
  const { retry } = useAuth()
  const navigate = useNavigate()

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [newsletterAccepted, setNewsletterAccepted] = useState(false)
  const [signupCode, setSignupCode] = useState(initSignupCode || "")
  const [emailError, setEmailError] = useState("")
  const [passwordError, setPasswordError] = useState("")
  const [signupCodeError, setSignupCodeError] = useState("")
  const [disabled, setDisabled] = useState(false)

  const formSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    setEmailError("")
    setPasswordError("")
    setSignupCodeError("")

    setDisabled(true)
    const { data } = await api().post("/registration", {
      user: {
        email_address: email,
        password: password,
        password_confirmation: password,
      },
      signup_code: signupCode,
      newsletterAccepted: newsletterAccepted,
    })

    if (data.status === "ok") {
      setEmailError("")
      setPasswordError("")
      setSignupCodeError("")

      await retry()
      navigate(data.redirect_to)
    } else if (data.status === "error" && data.errors) {
      data.errors.forEach((error: ApiFieldError) => {
        if (error.field === "email_address") {
          setEmailError(error.message)
        } else if (error.field === "password") {
          setPasswordError(error.message)
        } else if (error.field === "signup_code") {
          setSignupCodeError(error.message)
        }
      })
      setDisabled(false)
    }
  }

  return (
    <>
      <div className="flex min-h-full flex-col justify-start py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-200">
            Sign up
          </h2>
          <div className="mt-3 text-sm text-center">
            <Link to="/app/login" className="font-medium text-indigo-600 hover:text-indigo-500">
              Log in instead
              <FontAwesomeIcon icon={faArrowRight} className="ml-1 text-xs" />
            </Link>
          </div>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white dark:bg-gray-900 px-4 py-8 shadow sm:rounded-lg sm:px-10">
            <div className="mb-6 text-sm dark:text-gray-300">
              We are currently accepting sign ups through invitation only. If you'd like be a beta
              tester, please
              <a
                className="underline text-blue-500 dark:text-blue-300"
                href="mailto:support@nanamap.com"
              >
                email us
              </a>
              !
            </div>
            <form className="space-y-6" onSubmit={formSubmit} method="POST">
              <Input
                label="Beta tester invitation code"
                name="signup_code"
                type="text"
                error={signupCodeError}
                onChange={(newValue) => setSignupCode(newValue)}
                disabled={disabled}
                value={signupCode}
                inputClassName="font-mono"
              />

              <Input
                label="Email address"
                name="email"
                type="text"
                error={emailError}
                onChange={(newValue) => setEmail(newValue)}
                disabled={disabled}
                value={email}
              />

              <Input
                label="Password"
                name="password"
                type="password"
                error={passwordError}
                onChange={(newValue) => setPassword(newValue)}
                disabled={disabled}
                value={password}
              />

              <div className="text-sm">
                By signing up, I agree to Nana's{" "}
                <a
                  href="/policies/terms"
                  target="_blank"
                  className="underline text-blue-500 dark:text-blue-300"
                >
                  Terms of Use
                </a>{" "}
                and{" "}
                <a
                  href="/policies/privacy"
                  target="_blank"
                  className="underline text-blue-500 dark:text-blue-300"
                >
                  Privacy Policy
                </a>
                .
              </div>

              <div className="text-xs flex items-center">
                <input
                  type="checkbox"
                  id="newsletter-acceptance"
                  data-testid="newsletter-checkbox"
                  checked={newsletterAccepted}
                  onChange={(e) => {
                    setNewsletterAccepted(e.target.checked)
                  }}
                />
                <label className="ml-2 cursor-pointer" htmlFor="newsletter-acceptance">
                  Send me updates about Nana by email, max once a week.
                </label>
              </div>

              <div>
                <button
                  type="submit"
                  className={classNames(
                    "flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-500"
                  )}
                  disabled={disabled}
                >
                  Sign up
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}
